import clsx from 'clsx'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import HouseSearchResultsCardPrices from './house-search-results-card-prices/HouseSearchResultsCardPrices'
import { Capacity } from '../../capacity'
import { CapacityModeEnum } from '../../capacity/capacity.enum'
import { HouseEquipment } from '../../house-equipment/HouseEquipment'
import { getHouseTypeLabel } from '../../house-search/house-search.utils'
import { Modal } from '../../modal'
import { Rating } from '../../rating/Rating'
import { TextTravelTime } from '../../text-travel-time/TextTravelTime'
import {
  HouseSearchFragment,
  HouseStatus,
  NavigationTargetEnum,
} from '../../../../lib/graphql'
import { IHouseExplorerUrlProps } from '../../../../utils/house-explorer-url-params.helper'
import { computeHouseAddress } from '../../../../utils/houses/addresses/compute-house-address.util'
import { NextImage } from '../../../atoms'
import { useTravelTypeContext } from '../../../contexts'
import CategoryHouseEquipment from '../../../organisms/house-equipments/category-house-equipments/category-house-equipments'
import OwnerResponsePopover from '../../../organisms/owner-response-popover/OwnerResponsePopover'
import ProposalGallery from '../../../organisms/proposal/proposal-gallery/ProposalGallery'

export interface IHouseSearchResultsCard {
  house: HouseSearchFragment
  index: number
  numberOfNights?: number
  numberOfDays?: number
  createProposalClick?: (house: HouseSearchFragment, index: number) => void
  hasCarousel?: boolean
  onHouseClick: (params: IHouseExplorerUrlProps) => void
  destination?: string | null
  isMapMode?: boolean
  mapHoveredHouse?: string | undefined
  setMapHoveredHouse?: Dispatch<SetStateAction<string | undefined>>
}

export const HouseSearchResultsCard: React.FC<IHouseSearchResultsCard> = ({
  house,
  index,
  numberOfNights,
  numberOfDays,
  createProposalClick,
  hasCarousel,
  onHouseClick,
  destination,
  isMapMode,
  mapHoveredHouse,
  setMapHoveredHouse,
}) => {
  const [isEquipmentsModalOpen, setIsEquipmentsModalOpen] =
    useState<boolean>(false)
  const { travelType } = useTravelTypeContext()

  const hasDraftHouse = house.status === HouseStatus.DRAFT

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (hasDraftHouse) {
      return
    }

    if (createProposalClick) {
      createProposalClick(house, index)
    }
  }

  const houseAddress = computeHouseAddress({ address: house.address })

  // Determine if this house should be highlighted
  const isSelected = house._id === mapHoveredHouse

  const houseEquipments = (house?.equipments || []).slice(0, 3)

  const openHousePage = () => {
    if (hasDraftHouse) {
      return
    }

    onHouseClick({
      _id: house._id,
      pathname: house.pathname,
      index,
      target: NavigationTargetEnum.HOUSE_PAGE,
      suggestion: house?.suggestion,
      travelType,
    })
  }

  return (
    <div
      className={clsx(
        s.container,
        { [s.withoutCarousel]: !hasCarousel },
        { [s.mapContainer]: isMapMode },
        { [s.highlight]: isSelected },
      )}
      onMouseEnter={() => setMapHoveredHouse && setMapHoveredHouse(house._id)}
      onMouseLeave={() => setMapHoveredHouse && setMapHoveredHouse(undefined)}
    >
      {!hasCarousel ? (
        <button onClick={openHousePage} className={s.imageContainer}>
          <NextImage
            src={house.pictures[0].uri || ''}
            alt={house.title}
            sizes='(max-width: 768px) 85vw, 15vw'
          />
        </button>
      ) : (
        <>
          <div className={s.proposalGalleryContainer}>
            <ProposalGallery
              housePictures={house.pictures}
              positiveVoteCount={0}
              negativeVoteCount={0}
              itemContainerClassName={s.itemContainerClassName}
              mainCarouselClassName={s.mainCarouselClassName}
              isAutonomousBookingAvailable={
                house.isAutonomousBookingAvailable ?? false
              }
              onClickImage={openHousePage}
            />
          </div>
          <div className={s.imageContainer}>
            <NextImage
              src={house.pictures[0].uri || ''}
              alt={house.title}
              className={s.image}
              sizes='(max-width: 768px) 85vw, 15vw'
            />
          </div>
        </>
      )}

      <div className={s.content}>
        <button onClick={openHousePage} className={s.houseAdressAndTitle}>
          <div className={s.rightContent}>
            <div>
              <div className={s.city}>
                <span>{houseAddress}</span>
              </div>
              <div className={s.title}>{house.title}</div>
            </div>
            {house &&
              house.status === HouseStatus.PUBLISHED &&
              (house.ownerAnswerRate !== 0 || house.ownerAnswerTime !== 0) && (
                <div>
                  <OwnerResponsePopover
                    answerRate={house.ownerAnswerRate}
                    avgAnswerTime={house.ownerAnswerTime}
                  />
                </div>
              )}
          </div>
        </button>

        <div className={s.infoAndPriceContainer}>
          <div className={s.houseInfoContainer}>
            <Rating
              rating={house?.googleRating?.rating}
              userRatingsTotal={house?.googleRating?.userRatingsTotal}
            />
            <p className={s.subTitle}>
              {getHouseTypeLabel(house.houseType || null)}
            </p>
            <Capacity
              beds={house?.numberOfBeds ?? 0}
              bedrooms={house?.numberOfBedrooms ?? 0}
              capacity={house.capacity ?? 0}
              className={s.capacity}
              subClassName={s.subCapacity}
              dailyMaxCapacity={house?.options?.studyDay.options?.capacity.max}
              corporateEveningEventMaxCapacity={
                house.options?.corporateEvening.options?.capacity.max
              }
              numberOfWorkspaces={house?.numberOfWorkspaces ?? 0}
              mode={CapacityModeEnum.HOUSE_SEARCH}
              isMapMode={isMapMode}
            />
            {hasCarousel && (
              <>
                {houseEquipments.length > 0 ? (
                  <div className={s.equipmentsWrapper}>
                    <div className={s.list}>
                      {houseEquipments.map((equipment, index) => (
                        <HouseEquipment
                          key={equipment._id}
                          equipment={equipment}
                          withSeparator={
                            houseEquipments.length > 1 && index > 0
                          }
                        />
                      ))}
                    </div>

                    <p
                      className={s.showMore}
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsEquipmentsModalOpen(true)
                      }}
                    >
                      <FormattedMessage
                        id='ui.search.seeAllEquipments'
                        defaultMessage='Voir tous les équipements'
                      />
                    </p>
                  </div>
                ) : null}

                <TextTravelTime
                  destination={destination}
                  travelTimeInSecondForDriving={
                    house?.suggestion?.travelTimeInSecondForDriving ?? 0
                  }
                  travelTimeInSecondForTransit={
                    house?.suggestion?.travelTimeInSecondForTransit ?? 0
                  }
                />
              </>
            )}
            <Modal
              isOpen={isEquipmentsModalOpen}
              closeModal={(e) => {
                e.stopPropagation()
                setIsEquipmentsModalOpen(false)
              }}
            >
              <CategoryHouseEquipment
                title={
                  <FormattedMessage
                    id='ui.search.results.leasureEquipments'
                    defaultMessage='Les équipements de loisir'
                  />
                }
                equipments={house?.equipments ?? []}
                className=''
              />
            </Modal>
          </div>

          <HouseSearchResultsCardPrices
            house={house}
            onClick={onClick}
            numberOfNights={numberOfNights}
            numberOfDays={numberOfDays}
            travelType={travelType}
            hasDraftHouse={hasDraftHouse}
          />
        </div>
      </div>
    </div>
  )
}
